import type { CardCondition } from "@/context/AppContext";
import { apiFetch } from "@/lib/apiClient";

// Re-exported so existing call sites can keep importing `mapRarity` / `mapFinish`
// from `@/features/tcgApi`. The implementations live in `@tcg-web/normalize`
// so the API server uses the same logic — see packages/normalize/src/rarity.ts.
export { mapFinish, mapRarity } from "@tcg-web/normalize";

export interface TcgApiCard {
  id: string;
  name: string;
  supertype?: string;
  subtypes?: string[];
  hp?: string;
  types?: string[];
  number: string;
  artist?: string;
  rarity?: string;
  nationalPokedexNumbers?: number[];
  set: { id: string; name: string; series: string };
  tcgplayer?: {
    prices?: Record<
      string,
      {
        market?: number;
        mid?: number;
        low?: number;
        high?: number;
        directLow?: number;
      }
    >;
  };
}

export interface TcgPriceVariant {
  type: string;
  label: string;
  market: number;
  low?: number;
  high?: number;
}

export const PRICE_TYPE_LABELS: Record<string, string> = {
  normal: "Normal",
  holofoil: "Holo",
  reverseHolofoil: "Rev. Holo",
  "1stEditionHolofoil": "1st Ed. Holo",
  "1stEditionNormal": "1st Ed. Normal",
  unlimitedHolofoil: "Unlimited Holo",
};

export const CONDITION_FACTORS: Record<CardCondition, number> = {
  NM: 1.0,
  LP: 0.8,
  MP: 0.65,
  HP: 0.45,
  Damaged: 0.25,
};

/**
 * Escapes Lucene special characters that would break wildcard queries.
 * Preserves * and ? so we can use them as wildcards.
 */
export function escapeLucene(term: string): string {
  return term.replace(/[+\-!(){}\[\]^"~?:\\/]/g, "\\$&");
}

/**
 * Sends a Lucene query to the Pokémon TCG API and returns matching cards.
 */
export async function queryTcgApi(luceneQuery: string): Promise<TcgApiCard[]> {
  const url =
    `https://api.pokemontcg.io/v2/cards` +
    `?q=${encodeURIComponent(luceneQuery)}` +
    `&select=id,name,supertype,subtypes,hp,types,number,artist,rarity,nationalPokedexNumbers,set,tcgplayer` +
    `&pageSize=15`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return Array.isArray(json.data) ? json.data : [];
}

/**
 * Builds the right Lucene query for any card name, handling spaces and special chars.
 * - Single words  → wildcard:  name:*charizard*
 * - Multi-word    → phrase:    name:"mr. mime"  (falls back to first-word wildcard)
 */
export async function searchCards(raw: string): Promise<TcgApiCard[]> {
  const words = raw.trim().split(/\s+/);
  if (words.length === 1) {
    return queryTcgApi(`name:*${escapeLucene(raw.trim())}*`);
  }
  const phrase = raw.trim().replace(/"/g, "");
  const cards = await queryTcgApi(`name:"${phrase}"`);
  if (cards.length > 0) return cards;
  // Fallback: first significant word
  const first = escapeLucene(words[0].replace(/[.,']$/g, ""));
  return first.length >= 2 ? queryTcgApi(`name:*${first}*`) : [];
}

/**
 * Resolves the best available price from a TCGplayer price entry.
 * Priority: market → mid → average of (low + high) → null.
 * TCGplayer only computes `market` when there are enough recent sales,
 * so `mid` or the low/high midpoint are used as fallbacks for low-volume cards.
 */
function resolvePrice(p: {
  market?: number;
  mid?: number;
  low?: number;
  high?: number;
}): number | null {
  if (p.market != null) return p.market;
  if (p.mid != null) return p.mid;
  if (p.low != null && p.high != null)
    return Math.round(((p.low + p.high) / 2) * 100) / 100;
  return null;
}

/**
 * Returns all price variants with any resolvable price for a card, sorted by value.
 * Uses market price where available; falls back to mid or (low+high)/2 for cards
 * that TCGplayer hasn't computed a market price for yet.
 */
export function getPriceVariants(card: TcgApiCard): TcgPriceVariant[] {
  const prices = card.tcgplayer?.prices ?? {};
  const result: TcgPriceVariant[] = [];
  for (const [type, p] of Object.entries(prices)) {
    const price = resolvePrice(p);
    if (price == null) continue;
    result.push({
      type,
      label: PRICE_TYPE_LABELS[type] ?? type,
      market: price,
      low: p.low,
      high: p.high,
    });
  }
  return result.sort((a, b) => b.market - a.market);
}

/**
 * Returns the single "best" price for a card (highest market price variant).
 */
export function getBestPrice(card: TcgApiCard): TcgPriceVariant | null {
  const variants = getPriceVariants(card);
  return variants[0] ?? null;
}

/**
 * Applies a condition price factor to a NM market price.
 */
export function conditionAdjust(
  nmPrice: number,
  condition: CardCondition,
): number {
  return nmPrice * CONDITION_FACTORS[condition];
}

// ── TCG Sets API ───────────────────────────────────────────────────────────

export interface TcgSet {
  id: string;
  name: string;
  series: string;
  releaseDate: string;
  total: number;
  printedTotal?: number;
  ptcgoCode?: string;
}

export interface TcgSetCard {
  id: string;
  name: string;
  supertype?: string;
  subtypes?: string[];
  hp?: string;
  types?: string[];
  number: string;
  artist?: string;
  rarity?: string;
  nationalPokedexNumbers?: number[];
  set?: { series?: string };
  images?: { small?: string; large?: string };
  tcgplayer?: {
    prices?: Record<string, { market?: number }>;
  };
}

/**
 * Search for TCG sets by name. Accepts an optional AbortSignal for cancellation.
 */
export async function searchSets(
  query: string,
  signal?: AbortSignal,
): Promise<TcgSet[]> {
  const q = query.trim();
  const filter = q.length >= 2 ? `?q=name:*${escapeLucene(q)}*&` : "?";
  const url = `https://api.pokemontcg.io/v2/sets${filter}orderBy=-releaseDate&select=id,name,series,releaseDate,total,printedTotal,ptcgoCode&pageSize=40`;
  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    signal,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return Array.isArray(json.data) ? (json.data as TcgSet[]) : [];
}

/**
 * Fetch all cards in a set, sorted by card number. Accepts an optional AbortSignal.
 */
export async function fetchSetCards(
  setId: string,
  signal?: AbortSignal,
): Promise<TcgSetCard[]> {
  const url =
    `https://api.pokemontcg.io/v2/cards` +
    `?q=set.id:${setId}` +
    `&select=id,name,supertype,subtypes,hp,types,number,artist,rarity,nationalPokedexNumbers,set,images,tcgplayer` +
    `&orderBy=number&pageSize=250`;
  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    signal,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return Array.isArray(json.data) ? (json.data as TcgSetCard[]) : [];
}

/**
 * Bulk-add pricing rules:
 *  - Common   → flat $0.01
 *  - Uncommon → flat $0.02
 *  - Everything else → TCG API market price (may be null if unavailable)
 */
export function getBulkCardPrice(card: TcgSetCard): number | null {
  const r = (card.rarity ?? "").toLowerCase();
  if (r === "common") return 0.01;
  if (r === "uncommon") return 0.02;
  return getSetCardPrice(card);
}

/** True when the bulk price is a fixed flat rate rather than a TCG market lookup. */
export function isBulkFlatPrice(card: TcgSetCard): boolean {
  const r = (card.rarity ?? "").toLowerCase();
  return r === "common" || r === "uncommon";
}

/**
 * Fetches the current market price for a card identified by setCode + cardNumber.
 * Tries direct ID lookup first, then falls back to a query.
 */
export async function fetchCardMarketPrice(
  setCode: string,
  cardNumber: string,
  cardName?: string,
): Promise<number | null> {
  try {
    if (setCode && cardNumber) {
      const cardId = `${setCode.toLowerCase()}-${cardNumber}`;
      const url = `https://api.pokemontcg.io/v2/cards/${encodeURIComponent(cardId)}?select=tcgplayer`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (res.ok) {
        const json = await res.json();
        const c = json.data as TcgApiCard | undefined;
        if (c) {
          const price = getBestPrice(c);
          if (price) return price.market;
        }
      }
      const cards = await queryTcgApi(`set.id:${setCode} number:${cardNumber}`);
      if (cards.length > 0) {
        const price = getBestPrice(cards[0]);
        if (price) return price.market;
      }
    }
    if (cardName) {
      const cards = await searchCards(cardName);
      if (cards.length > 0) {
        const price = getBestPrice(cards[0]);
        if (price) return price.market;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Fetches just the supertype/subtypes/rarity metadata for a card by setCode +
 * cardNumber. Returns null on any error or 404 so callers can skip silently.
 * Used by AppContext to recover supertype on legacy card records.
 *
 * TNK-108: session-scoped negative cache. pokemontcg.io legitimately doesn't
 * carry every card we've imported (some legacy / promo / regional printings
 * are missing). The catalog-enrichment effect would otherwise re-fire the
 * same 404s every AppContext mount, spamming DevTools and the upstream API.
 * One Map per page-load is enough — short-circuit on cache hits without
 * touching the network.
 */
const missCache = new Set<string>();

/**
 * Session-scoped rate-limit circuit breaker. pokemontcg.io has no
 * auth path for our usage and throttles aggressively (HTTP 429).
 * Once we hit ANY 429, stop firing for the rest of the page load —
 * the worker pool in AppContext catalog-backfill would otherwise
 * blast thousands of doomed requests and spam DevTools. Reset
 * happens on full page reload; legacy rows just enrich on a future
 * visit when the upstream calms down.
 */
let upstreamRateLimited = false;

/** True if a prior call in this session received a 429. Callers
 *  can short-circuit their own enrichment loops without fishing
 *  through promises for the signal. */
export function isUpstreamRateLimited(): boolean {
  return upstreamRateLimited;
}

/**
 * TNK-141 phase D — server-routed catalog lookup.
 *
 * Hits our `/api/card-catalog/pokemon/by-id` endpoint instead of going
 * direct to pokemontcg.io. The server keeps a DB cache + rate-aware
 * throttle (header-driven, with min-interval + safety buffer + 429
 * cooldown) so a fresh browser session can't single-handedly torch the
 * shared IP quota anymore. The legacy session-scoped breaker stays as
 * belt-and-suspenders — fires if the server returns 429 anyway.
 *
 * Return shape preserved (`supertype`/`subtypes`/`rarity`/`hp`/`types`/
 * `artist`/`nationalPokedexNumbers`/`set.series`) so the AppContext
 * caller doesn't need rewriting; we map the server's ResolvedCard
 * into the legacy shape here.
 */
export async function fetchCardMeta(
  setCode: string,
  cardNumber: string,
): Promise<{
  supertype?: string;
  subtypes?: string[];
  rarity?: string;
  hp?: string;
  types?: string[];
  artist?: string;
  nationalPokedexNumbers?: number[];
  set?: { series?: string };
} | null> {
  try {
    if (!setCode || !cardNumber) return null;
    if (upstreamRateLimited) return null;
    const numPart = cardNumber.split("/")[0];
    const normalisedNumber = /^\d+$/.test(numPart)
      ? String(parseInt(numPart, 10))
      : numPart;
    const cacheKey = `${setCode.toLowerCase()}-${normalisedNumber}`;
    if (missCache.has(cacheKey)) return null;
    const url =
      `/api/card-catalog/pokemon/by-id` +
      `?setCode=${encodeURIComponent(setCode)}` +
      `&number=${encodeURIComponent(normalisedNumber)}`;
    const res = await apiFetch(url, {
      headers: { Accept: "application/json" },
    });
    if (res.status === 429) {
      // Server told us the upstream is throttled — short-circuit the
      // rest of this session so we don't keep hammering the proxy.
      if (!upstreamRateLimited) {
        upstreamRateLimited = true;
        console.warn(
          "[tcgApi] catalog proxy rate-limited (429) — pausing per-card enrichment for this session",
        );
      }
      return null;
    }
    if (!res.ok) {
      missCache.add(cacheKey);
      return null;
    }
    const json = (await res.json()) as {
      card: {
        supertype?: string;
        subtypes?: string[];
        rarity?: string;
        hp?: string;
        types?: string[];
        artist?: string;
        pokedexNumbers?: number[];
        series?: string;
      } | null;
    };
    const c = json.card;
    if (!c) {
      missCache.add(cacheKey);
      return null;
    }
    // Map server's ResolvedCard shape to the legacy fetchCardMeta
    // return shape — keeps AppContext's consumer code unchanged.
    return {
      supertype: c.supertype,
      subtypes: c.subtypes,
      rarity: c.rarity,
      hp: c.hp,
      types: c.types,
      artist: c.artist,
      nationalPokedexNumbers: c.pokedexNumbers,
      set: c.series ? { series: c.series } : undefined,
    };
  } catch {
    return null;
  }
}

export interface ResolvedCardByNameAndNumber {
  setId: string;
  setName: string;
  rarity?: string;
  supertype?: string;
  subtypes?: string[];
  typeLine?: string;
  hp?: string;
  // TNK-120: extended catalog surface. All optional — older catalog rows
  // cached before the resolver was extended return undefined.
  artist?: string;
  language?: string;
  finishes?: string[];
  releasedAt?: string;
  // TNK-121: MTG-native fields. Populated only when the catalog row was
  // resolved through the Magic resolver.
  mtgManaCost?: string;
  mtgPowerToughness?: string;
  mtgLoyalty?: string;
  mtgOracleText?: string;
  mtgColors?: string[];
}

/**
 * Resolve a card to its canonical printing when the local record has a known
 * name + number but no (or a junk) setCode. Used by the AppContext backfill
 * to recover proper set + rarity for cards imported from coarse buckets
 * (e.g. set "Deck Exclusives" / "Sword & Shield Promo").
 *
 * Routes through our own `/api/card-catalog/resolve` — a DB-cached, server-
 * side proxy in front of the upstream API. First lookup of a card populates
 * the cache; repeats (any session/user) are a fast indexed DB read. The
 * server handles number normalization ("56/072" → "56"), `/total`-based
 * disambiguation, and game-specific upstream selection.
 *
 * `game` selects the upstream resolver (default "Pokemon"). Returns null on
 * any error, no match, or a game with no resolver wired yet.
 */
export async function searchCardByNameAndNumber(
  cardName: string,
  cardNumber: string,
  game = "Pokemon",
  setName?: string,
): Promise<ResolvedCardByNameAndNumber | null> {
  // TNK-123: legacy Magic sets (Ice Age, Fifth Edition, Fourth Edition,
  // etc.) often came in with empty collector numbers — Scryfall back-
  // assigned them but our import path doesn't always carry them.
  // Server falls through to a name+set Scryfall search when number is
  // empty and setName is supplied, so allow the call to proceed in
  // that case.
  if (!cardName) return null;
  if (!cardNumber && !(game === "Magic" && setName)) return null;
  try {
    const params = new URLSearchParams({ game, name: cardName });
    if (cardNumber) params.set("number", cardNumber);
    if (setName) params.set("setName", setName);
    const res = await apiFetch(
      `/api/card-catalog/resolve?${params.toString()}`,
    );
    if (!res.ok) return null;
    const json = (await res.json()) as {
      card: {
        setId: string;
        setName: string;
        rarity?: string;
        supertype?: string;
        subtypes?: string[];
        typeLine?: string;
        hp?: string;
        artist?: string;
        language?: string;
        finishes?: string[];
        releasedAt?: string;
        mtgManaCost?: string;
        mtgPowerToughness?: string;
        mtgLoyalty?: string;
        mtgOracleText?: string;
        mtgColors?: string[];
      } | null;
    };
    if (!json.card) return null;
    return {
      setId: json.card.setId,
      setName: json.card.setName,
      rarity: json.card.rarity,
      supertype: json.card.supertype,
      subtypes: json.card.subtypes,
      typeLine: json.card.typeLine,
      hp: json.card.hp,
      artist: json.card.artist,
      language: json.card.language,
      finishes: json.card.finishes,
      releasedAt: json.card.releasedAt,
      mtgManaCost: json.card.mtgManaCost,
      mtgPowerToughness: json.card.mtgPowerToughness,
      mtgLoyalty: json.card.mtgLoyalty,
      mtgOracleText: json.card.mtgOracleText,
      mtgColors: json.card.mtgColors,
    };
  } catch {
    return null;
  }
}

/** Get best market price from a TcgSetCard */
export function getSetCardPrice(card: TcgSetCard): number | null {
  const prices = card.tcgplayer?.prices ?? {};
  for (const key of [
    "holofoil",
    "reverseHolofoil",
    "normal",
    "1stEditionHolofoil",
    "unlimitedHolofoil",
  ]) {
    if (prices[key]?.market != null) return prices[key].market!;
  }
  const first = Object.values(prices).find((p) => p.market != null);
  return first?.market ?? null;
}

/**
 * Returns all available priced variants for a set card, sorted highest-price first.
 * Returns an empty array for flat-priced cards (common/uncommon) — the bulk-add flow uses FLAT_VARIANTS for these instead.
 */
export function getCardVariants(
  card: TcgSetCard,
): Array<{ type: string; label: string; price: number }> {
  const r = (card.rarity ?? "").toLowerCase();
  if (r === "common" || r === "uncommon") return [];
  const prices = card.tcgplayer?.prices ?? {};
  return Object.entries(prices)
    .filter(([, p]) => p.market != null)
    .map(([type, p]) => ({
      type,
      label: PRICE_TYPE_LABELS[type] ?? type,
      price: p.market!,
    }))
    .sort((a, b) => b.price - a.price);
}
